// src/components/admin/StaffMenuMapping.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Table, Button, Form, Modal,
  Spinner, Alert, Badge, InputGroup, Pagination
} from 'react-bootstrap';
import {
  FaUserCog, FaPlus, FaEdit, FaTrash, FaSave, FaTimes,
  FaShieldAlt, FaEye, FaPencilAlt, FaSearch, FaUniversity, FaArrowLeft, FaUserShield
} from 'react-icons/fa';

const StaffMenuMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Pagination & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);

  const [formData, setFormData] = useState({
    role_id: '',
    menu_id: '',
    can_view: true,
    can_edit: false,
    can_delete: false,
    college_id: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = !user.college_id;

  useEffect(() => {
    if (user?.college_id) {
      setSelectedCollege({ college_id: user.college_id, college_name: 'My College' });
    } else {
      fetchColleges();
    }
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/college');
      if (response.success && Array.isArray(response.message?.showCollege)) {
        setColleges(response.message.showCollege);
      }
    } catch (err) {
      setError('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  // Helper to extract unique menus from mappings list
  const getUniqueMenusFromMappings = (mappingList) => {
    const uniqueMap = new Map();
    if (Array.isArray(mappingList)) {
      mappingList.forEach(item => {
        if (item.menu_id && item.menu && !uniqueMap.has(item.menu_id)) {
          uniqueMap.set(item.menu_id, {
            menu_id: item.menu_id,
            menu_name: item.menu.menu_name
          });
        }
      });
    }
    return Array.from(uniqueMap.values());
  };

  // Fetch all required data
  const fetchData = async () => {
    if (!selectedCollege) return;
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch current mappings
      let mappingUrl = '/api/menu-mapping';
      if (selectedCollege.college_id) {
        mappingUrl = `/api/menu-mapping?collegeId=${selectedCollege.college_id}`;
      }
      const mappingRes = await apiService.get(mappingUrl);
      if (mappingRes.success && Array.isArray(mappingRes.message?.mappings)) {
        let fetchedMappings = mappingRes.message.mappings;
        if (selectedCollege.college_id === null) {
          fetchedMappings = fetchedMappings.filter(m => m.college_id === null);
        }
        setMappings(fetchedMappings);
      }

      // 2. Fetch roles (context-aware)
      let rolesUrl = selectedCollege.college_id ? `/api/user-admin/staff/${selectedCollege.college_id}` : '/api/roles';
      const rolesRes = await apiService.get(rolesUrl);
      if (rolesRes.success) {
        let roleList = rolesRes.message?.roles;
        if (Array.isArray(roleList)) {
          if (selectedCollege.college_id === null) {
            roleList = roleList.filter(r => r.college_id === null);
          }
          setRoles(roleList);
        }
      }

      // 3. Fetch menus (same for both)
      const menuRes = await apiService.get('/api/menus');
      if (menuRes.success && Array.isArray(menuRes.message?.menus)) {
        setMenus(menuRes.message.menus);
      }
    } catch (err) {
      setError('Error loading data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCollege) {
      fetchData();
    }
  }, [selectedCollege]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Fetch roles for dropdown based on selected college in modal
  const fetchRolesForDropdown = async (cId) => {
    try {
      let url = cId ? `/api/user-admin/staff/${cId}` : '/api/roles';
      const response = await apiService.get(url);
      if (response.success && Array.isArray(response.message?.roles)) {
        let fetchedRoles = response.message.roles;
        if (!cId) {
             fetchedRoles = fetchedRoles.filter(r => r.college_id === null);
        }
        setRoles(fetchedRoles);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setRoles([]);
    }
  };

  const openModal = (mapping = null) => {
    if (mapping) {
      setIsEditMode(true);
      setCurrentMapping(mapping);
      setFormData({
        role_id: mapping.role_id || '',
        menu_id: mapping.menu_id || '',
        can_view: mapping.can_view !== false,
        can_edit: mapping.can_edit || false,
        can_delete: mapping.can_delete || false,
        college_id: mapping.college_id || ''
      });
    } else {
      setIsEditMode(false);
      setCurrentMapping(null);
      setFormData({
        role_id: '',
        menu_id: '',
        can_view: true,
        can_edit: false,
        can_delete: false,
        college_id: ''
      });
    }
    if (!selectedCollege?.college_id) {
        fetchRolesForDropdown(mapping ? mapping.college_id : '');
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.role_id || !formData.menu_id) {
      setError('Role and Menu are required');
      return;
    }

    try {
      setError(null);
      setSuccessMsg('');

      const payload = {
        role_id: Number(formData.role_id),
        menu_id: Number(formData.menu_id),
        can_view: formData.can_view,
        can_edit: formData.can_edit,
        can_delete: formData.can_delete
      };

      if (selectedCollege?.college_id) {
        payload.college_id = selectedCollege.college_id;
      } else if (formData.college_id) {
        payload.college_id = Number(formData.college_id);
      } else {
        payload.college_id = null;
      }
      
      let response;
      if (isEditMode) {
        response = await apiService.patch('/api/menu-mapping', payload);
      } else {
        response = await apiService.patch('/api/menu-mapping', payload);
      }

      if (response.success) {
        setSuccessMsg(isEditMode ? 'Mapping updated successfully' : 'Mapping created successfully');
        setShowModal(false);
        fetchData();
      } else {
        setError(response.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
  };

  const handleDelete = async (mapping) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;

    try {
      const response = await apiService.delete(`/api/menu-mapping`, {
        data: {
          role_menu_id: mapping.role_menu_id
        }
      });

      if (response.success) {
        setSuccessMsg('Mapping deleted successfully');
        fetchData();
      } else {
        setError('Failed to delete mapping');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting mapping');
    }
  };

  // Filter mappings
  const filteredMappings = mappings.filter(mapping => 
    mapping.role?.role_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mapping.menu?.menu_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredMappings.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMappings.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const filteredColleges = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(collegeSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <FaUserCog className="me-2 text-primary" />
            Staff Menu Mapping
          </h2>
        </Col>
      </Row>

      {/* College Selection View */}
      {isSuperAdmin && !selectedCollege && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={collegeSearch}
              onChange={(e) => setCollegeSearch(e.target.value)}
            />
          </InputGroup>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row>
              {/* Non-College Tile */}
              <Col md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: null, college_name: 'System Mappings' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUserShield size={50} className="text-primary mb-3 mx-auto" />
                    <h5>System Mappings</h5>
                    <p className="text-muted small">Global / System-level mappings</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* College Tiles */}
              {filteredColleges.map(college => (
                <Col md={4} lg={3} key={college.college_id} className="mb-4">
                  <Card
                    className="shadow-sm h-100 text-center cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <Card.Body className="d-flex flex-column justify-content-center">
                      <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                      <h5>{college.college_name}</h5>
                      {college.college_code && (
                        <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {selectedCollege && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              {isSuperAdmin && (
                <Button
                  variant="outline-secondary"
                  className="mb-2"
                  onClick={() => {
                    setSelectedCollege(null);
                    setMappings([]);
                    setCollegeSearch('');
                    setSearchTerm('');
                  }}
                >
                  <FaArrowLeft className="me-2" /> Back to College List
                </Button>
              )}
              <h4 className="mb-0">
                Mappings for <strong>{selectedCollege.college_name}</strong>
              </h4>
            </div>
            <Button variant="primary" onClick={() => openModal()}>
              <FaPlus className="me-2" /> Add New Mapping
            </Button>
          </div>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Search */}
      {!loading && mappings.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by role or menu..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>
              </Col>
              <Col md={6} className="text-md-end">
                <Badge bg="light" text="dark" className="border">
                  Total: {filteredMappings.length}
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading mappings...</p>
        </div>
      ) : mappings.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <FaUserCog size={60} className="text-muted mb-3" />
            <h5 className="text-muted">No menu mappings found</h5>
            <p className="text-muted">Create your first mapping to assign permissions</p>
          </Card.Body>
        </Card>
      ) : currentItems.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <h5>No matching records found</h5>
          <p>Try adjusting your search terms</p>
        </div>
      ) : (
        <>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Table responsive hover className="align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Role</th>
                    <th>Menu</th>
                    <th>View</th>
                    <th>Edit</th>
                    <th>Delete</th>
                    <th>College</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((mapping, index) => (
                    <tr key={`${mapping.role_id}-${mapping.menu_id}`}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <strong>{mapping.role?.role_name || '—'}</strong>
                      </td>
                      <td>{mapping.menu?.menu_name || '—'}</td>
                      <td>{mapping.can_view ? <FaEye className="text-success" /> : <FaTimes className="text-danger" />}</td>
                      <td>{mapping.can_edit ? <FaPencilAlt className="text-success" /> : <FaTimes className="text-danger" />}</td>
                      <td>{mapping.can_delete ? <FaTrash className="text-success" /> : <FaTimes className="text-danger" />}</td>
                      <td>
                        {mapping.college_id ? (
                          <Badge bg="info">College {mapping.college_id}</Badge>
                        ) : (
                          <Badge bg="secondary">System</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openModal(mapping)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(mapping)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination size="sm">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }
                  return null;
                })}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Create/Edit Mapping Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit Menu Mapping' : 'Create New Menu Mapping'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {selectedCollege && (
              <Alert variant="info" className="py-2">
                Managing mappings for: <strong>{selectedCollege.college_name}</strong>
              </Alert>
            )}
            {!selectedCollege?.college_id && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>College (Optional)</Form.Label>
                    <Form.Select
                      name="college_id"
                      value={formData.college_id}
                      onChange={(e) => {
                        handleChange(e);
                        fetchRolesForDropdown(e.target.value);
                      }}
                    >
                      <option value="">Select College (System/Global)</option>
                      {colleges.map(college => (
                        <option key={college.college_id} value={college.college_id}>
                          {college.college_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.role_name} {role.college_id ? `(College ID: ${role.college_id})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Menu *</Form.Label>
                  <Form.Select
                    name="menu_id"
                    value={formData.menu_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Menu</option>
                    {(selectedCollege?.college_id ? getUniqueMenusFromMappings(mappings) : menus).map(menu => (
                      <option key={menu.menu_id} value={menu.menu_id}>
                        {menu.menu_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <h6 className="mb-3">Permissions</h6>

            <Row>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="can_view"
                  label="Can View"
                  checked={formData.can_view}
                  onChange={handleChange}
                  name="can_view"
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="can_edit"
                  label="Can Edit"
                  checked={formData.can_edit}
                  onChange={handleChange}
                  name="can_edit"
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="can_delete"
                  label="Can Delete"
                  checked={formData.can_delete}
                  onChange={handleChange}
                  name="can_delete"
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update Mapping' : 'Create Mapping'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StaffMenuMapping;